
import React, { memo, useEffect, useState } from 'react';
import './questions.css';
import { useKioskContext } from '../../store/Context';
import { useNavigate } from 'react-router-dom';
import { getQuestions, sendQuestionAnswers } from '../../Api/API';
import { toast } from 'react-toastify';
import Loader from '../../components/loader/Loader';

const Questions = memo(() => {
  const navigate = useNavigate();
  const { setCurrentScreen, restart, text } = useKioskContext();

  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [userDetails, setUserDetails] = useState({
    name: '',
    dob: '',
  });

  const [language, setLanguage] = useState('0');

  useEffect(() => {
    const loadQuestions = async () => {
      setLoading(true);

      try {
        const { data, error } = await getQuestions();

        if (error) {
          toast.error(error, { toastId: 'error' });
          return;
        }

        if (!Array.isArray(data)) {
          toast.error('Unable to load questions.', {
            toastId: 'error',
          });
          return;
        }

        setQuestions(data);
      } catch (error) {
        console.error('Get questions error:', error);
        toast.error('Something went wrong.', {
          toastId: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();

    const name = localStorage.getItem('participantName') || '';
    const offenderId = localStorage.getItem('offenderId') || '';
    const dob = localStorage.getItem('dob') || '';

    const dateParts = dob.split('-');

    let formattedDOB = '-';

    if (dateParts.length === 3) {
      formattedDOB =
        `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
    }

    setUserDetails({
      name: `${name} (#${offenderId})`,
      dob: formattedDOB,
    });

    setLanguage(localStorage.getItem('language') || '0');
  }, []);

  useEffect(() => {
    if (loading) {
      window.scrollTo(0, 0);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [loading]);

  const answerHandler = (questionId, index, answer) => {
    setAnswers((previousAnswers) => ({
      ...previousAnswers,
      [`questionId${index + 1}`]: questionId,
      [`answer${index + 1}`]: answer,
    }));
  };

  const checkIsActive = (index) => {
    return answers[`answer${index + 1}`];
  };

  const handleClick = async (type) => {
    if (type === 'back') {
      navigate('/');
      return;
    }

    if (type !== 'checkIn' || loading) {
      return;
    }

    if (
      questions.length === 0 ||
      questions.some(
        (_, index) => !answers[`answer${index + 1}`]
      )
    ) {
      toast.error(text.answerAll, {
        toastId: 'error',
      });
      return;
    }

    setLoading(true);

    try {
      // Build the 10 question/answer fields expected
      // by the existing Kiosk Check-In API.
      const request = {};

      for (let i = 1; i <= 10; i++) {
        request[`questionId${i}`] =
          answers[`questionId${i}`] || null;

        request[`answer${i}`] =
          answers[`answer${i}`] || null;
      }

      request.checkInType = 'Report Date';

      const { data, error } = await sendQuestionAnswers(request);

      if (error || !data?.success) {
        toast.error(error || 'Check-in failed.', {
          toastId: 'error',
        });
        return;
      }

      setCurrentScreen(4);
      navigate('/feedback');

    } catch (error) {
      console.error('Check-in error:', error);

      toast.error('Something went wrong.', {
        toastId: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const renderTop = () => (
    <div className="details-container">
      <div className="name">
        <span
          style={{
            width: language === '1' ? '250px' : '200px',
          }}
        >
          {text.name} :
        </span>

        <span>{userDetails.name}</span>
      </div>

      <div className="information">
        <span
          style={{
            width: language === '1' ? '250px' : '200px',
          }}
        >
          {text.dob} :
        </span>

        <span>{userDetails.dob}</span>
      </div>
    </div>
  );

  const renderQuestions = () => (
    <div className="questions-container">
      {questions.map((question, index) => (
        <div key={question.questionId}>
          <div className="question">
            <p
              style={{
                fontSize: '16px',
                fontWeight: 400,
              }}
            >
              {language === '1'
                ? question.questionSpanish || question.question
                : question.question}
            </p>

            <div className="button-container">
              <button
                style={{
                  background:
                    checkIsActive(index) === 'Yes'
                      ? 'black'
                      : 'transparent',
                  color:
                    checkIsActive(index) === 'Yes'
                      ? 'white'
                      : 'black',
                  fontSize: '12px',
                  fontWeight: 400,
                }}
                onClick={() =>
                  answerHandler(
                    question.questionId,
                    index,
                    'Yes'
                  )
                }
              >
                {text.yes}
              </button>

              <button
                style={{
                  background:
                    checkIsActive(index) === 'No'
                      ? 'black'
                      : 'transparent',
                  color:
                    checkIsActive(index) === 'No'
                      ? 'white'
                      : 'black',
                  fontSize: '12px',
                  fontWeight: 400,
                }}
                onClick={() =>
                  answerHandler(
                    question.questionId,
                    index,
                    'No'
                  )
                }
              >
                {text.no}
              </button>
            </div>
          </div>

          <div className="divider"></div>
        </div>
      ))}
    </div>
  );

  const renderButton = () => (
    <div className="bottom-button-container">
      <button
        onClick={() => handleClick('back')}
        className="hollow"
        disabled={loading}
      >
        {text.back}
      </button>

      <button
        className="hollow"
        onClick={restart}
        disabled={loading}
      >
        {text.restart}
      </button>

      <button
        onClick={() => handleClick('checkIn')}
        disabled={loading || questions.length === 0}
      >
        {text.checkIn}
      </button>
    </div>
  );

  return (
    <div>
      {loading && <Loader />}

      <div className="questions">
        {renderTop()}

        <p className="heading">
          {text.questionsPageTitle}
        </p>

        {renderQuestions()}

        {renderButton()}
      </div>
    </div>
  );
});

export default Questions;
